import { Capacitor } from "@capacitor/core";
import { Geolocation, Position } from "@capacitor/geolocation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "@uidotdev/usehooks";
import axios from "axios";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import useSWR from "swr";
import * as z from "zod";
import { genders, updateUser, updateUserValue } from "../lib/slices/userSlice";
import GoogleLogin from "./GoogleLogin";
import { PhotoFormField } from "./PhotoFormField";
import { Button } from "./ui/button";
import { AlcDrawer, DrawerTitle } from "./ui/drawer";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";

const FormSchema = z.object({
  name: z.string(),
  tag: z.string().regex(/(?:^[a-zA-Z0-9_.-]+$)|(?:^$)/, {
    message:
      "Tag může obsahovat pouze písmena, číslice, tečky, pomlčky a podtržítka.",
  }),
  gender: z.string(),
  weight: z.number().min(0).nullable(),
  photo: z.string().nullable(),
  behavior: z.object({
    locationSharing: z.boolean(),
    searchNearby: z.boolean(),
  }),
});

const fetcher = (token) => async (url) => {
  const res = await fetch(url, { headers: { Authorization: token } });

  if (!res.ok) {
    throw await res.json();
  }

  return res.json();
};

function SettingsForm({ setIsOpen }: { setIsOpen: (isOpen: boolean) => void }) {
  const dispatch = useDispatch();

  const data = useSelector((state: any) => state.user);

  const user = useSelector((state: any) => state.user);

  const [tagValid, setTagValid] = useState<boolean | undefined>(undefined);

  const form = useForm<z.infer<typeof FormSchema>>({
    mode: "onBlur",
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: data.name ?? "",
      tag: data.tag ?? "",
      gender: data.gender ?? "",
      weight: data.weight ?? null,
      photo: data.photo ?? null,
      behavior: {
        locationSharing: data.behavior?.locationSharing ?? false,
        searchNearby: data.behavior?.searchNearby ?? false,
      },
    },
    errors:
      tagValid === false
        ? {
            tag: {
              type: "notAvailable",
              message: "Tag je již obsazen.",
            },
          }
        : undefined,
  });

  const tagDebounced = useDebounce(form.getValues("tag"), 500);

  const {
    data: { valid: tagValidSwr } = {},
    isLoading: tagValidLoading,
    error: tagValidError,
  } = useSWR<{ valid: boolean }>(
    tagDebounced &&
      tagDebounced.length >= 3 &&
      form.getFieldState("tag").isDirty &&
      z
        .string()
        .regex(/(?:^[a-zA-Z0-9_.-]+$)|(?:^$)/)
        .safeParse(tagDebounced).success
      ? process.env.REACT_APP_API_URL +
          "/users/tag/valid/" +
          encodeURIComponent(tagDebounced)
      : null,
    fetcher(user.token)
  );

  useEffect(() => {
    setTagValid(tagValidSwr);
  }, [tagValidSwr]);

  const [saving, setSaving] = useState(false);
  async function onSubmit(formData: z.infer<typeof FormSchema>) {
    setSaving(true);
    const { tag, ...newData } = _.omitBy(
      formData,
      (v) => v === null || v === ""
    );

    if ((tag || undefined) !== (data.tag || undefined)) {
      const res = await axios
        .post(
          `${process.env.REACT_APP_API_URL}/users/tag/set`,
          {
            tag,
          },
          {
            headers: {
              Authorization: user.token,
            },
          }
        )
        .catch(() => {
          toast.error("Nastala chyba při změně tagu.");
        });

      if (res && res?.data?.success) {
        dispatch(updateUserValue({ key: "tag", value: tag }));
      }
    }

    dispatch(updateUser(newData));

    setSaving(false);
    setIsOpen(false);
  }

  const [geoLocationError, setGeolocationError] = useState<
    string | undefined
  >();
  const [location, setLocation] = useState<
    GeolocationPosition | Position | undefined
  >();

  function getLocation() {
    setGeolocationError(undefined);
    if (Capacitor.isNativePlatform()) {
      Geolocation.getCurrentPosition()
        .then(setLocation)
        .catch((error) => {
          console.error(`Souradnice`, error);
          return setGeolocationError("Povol prosím aplikaci přístup k poloze.");
        });
    } else {
      if (!navigator.geolocation)
        return setGeolocationError(
          "Geolokace není na tomto zařízení podporována"
        );
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation(position);
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              return setGeolocationError(
                "Povol prosím geolokaci v prohlížeči pro tuto aplikaci."
              );
            case error.POSITION_UNAVAILABLE:
              return setGeolocationError(
                "Informace o poloze není k dispozici."
              );
            case error.TIMEOUT:
              return setGeolocationError(
                "Informace o poloze není k dispozici."
              );
            default:
              return setGeolocationError("Nastala neznámá chyba.");
          }
        }
      );
    }
  }

  const locationSharing = form.getValues("behavior.locationSharing");
  useEffect(() => {
    setGeolocationError(undefined);
    setLocation(undefined);
    if (locationSharing) getLocation();
  }, [locationSharing]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Jméno</FormLabel>
              <Input
                type="text"
                autoComplete="name"
                id="name"
                {...field}
                minLength={3}
                maxLength={20}
                required={false}
              />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tag"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tag</FormLabel>
              <Input
                type="text"
                {...field}
                minLength={3}
                maxLength={20}
                required={false}
              />
              <FormMessage />
              <FormDescription>
                Tag je unikátní uživatelské jméno, díky kterému tě ostatní
                uživatelé mohou najít.
                {tagValidLoading || tagDebounced !== form.getValues("tag") ? (
                  <>
                    <br />
                    Kontrola dostupnosti tagu...
                  </>
                ) : (
                  <>
                    {tagValidError && (
                      <>
                        <br />
                        Nastala chyba při kontrole tagu.
                      </>
                    )}
                    {tagValid === true && (
                      <>
                        <br />
                        Tag je dostupný.
                      </>
                    )}
                  </>
                )}
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="gender"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pohlaví</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value ?? ""}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={genders.Male}>Muž</SelectItem>
                  <SelectItem value={genders.Female}>Žena</SelectItem>
                  <SelectItem value={genders.NotSpecified}>
                    Nespecifikováno
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="weight"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Váha (kg)</FormLabel>
              <Input
                type="number"
                autoComplete="age"
                id="age"
                defaultValue={field.value ?? ""}
                onChange={(event) =>
                  field.onChange(+event.target.value || null)
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="photo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Obrázek</FormLabel>
              <PhotoFormField field={field} maxSize={200} />
              <FormMessage />
            </FormItem>
          )}
        />
        <h3 className="pt-4 font-medium">Propojení s Google účtem</h3>
        <div
          style={{
            color: "hsl(var(--muted-foreground))",
            margin: 0,
            fontSize: "0.8rem",
          }}
        >
          Propojení s Google účtem vám umožní obnovit data po reinstalaci
          aplikace
        </div>
        <GoogleLogin />
        <h3 className="pt-4 font-medium">Chování aplikace</h3>
        <FormField
          control={form.control}
          name="behavior.locationSharing"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
              <div className="space-y-0.5">
                <FormLabel>Záznam polohy</FormLabel>
                <FormDescription
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  Záznam polohy pro zobrazení v historii.
                  {!!locationSharing && (
                    <>
                      {location && (
                        <span style={{ color: "var(--success)" }}>
                          Získání polohy pracuje správně
                        </span>
                      )}
                      {geoLocationError && (
                        <span style={{ color: "var(--error)" }}>
                          {geoLocationError}
                        </span>
                      )}
                      {!location && !geoLocationError && (
                        <span style={{ color: "var(--pending)" }}>
                          Získávání polohy
                        </span>
                      )}
                    </>
                  )}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    if (!checked) form.setValue("behavior.searchNearby", false);
                    field.onChange(checked);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="behavior.searchNearby"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
              <div className="space-y-0.5">
                <FormLabel>Vyhledávání lidí v okolí</FormLabel>
                <FormDescription
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  Zobrazení lidí ve tvém okolí.
                  {!locationSharing && (
                    <span style={{ color: "var(--error)" }}>
                      Záznam polohy musí být povolen
                    </span>
                  )}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!locationSharing}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="py-4">
          <Button type="submit" className="w-full" disabled={saving}>
            Uložit
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default function UserSettings({
  children,
  asChild,
}: {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AlcDrawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      asChild={asChild}
      header={<DrawerTitle>Uživatelské nastavení</DrawerTitle>}
      trigger={children}
    >
      <SettingsForm setIsOpen={setIsOpen} />
    </AlcDrawer>
  );
}
